import { useFirebase } from 'components/Firebase/index.js';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useFeatureContext } from 'contexts/FeatureContext.jsx';
import presistanceHelper from 'helpers/localPersistenceHelper.js';

/**
 * @callback DataParser
 * @param {import('superagent').Response} data The data returned from API request
 * @return {any}
 */

/**
 * @typedef {import('react-query').UseQueryOptions & {
 *  params: {[param: string]: string},
 *  dataParser: DataParser
 * }} ClientAPIOptions
 */

/**
 * @template T
 * @typedef {import('react-query').UseQueryResult<T>} ClientAPIResult
 */


export const useInsightsClientAPI = (path, {
  params,
  dataParser = data => data.body,
  ...options
}) => {
  const { features: { oneDaySalesWindowEnabled, betaInsightsEnabled } } = useFeatureContext();
  let bigqueryProjectId;
  if (betaInsightsEnabled) {
    const betaInsightsBigQueryProjectId = presistanceHelper.getItem({ key: 'betaInsightsBigQueryProjectId' });
    bigqueryProjectId = betaInsightsBigQueryProjectId || 'bambuser-analytics-beta';
  }

  params = {
    bigqueryProjectId,
    oneDaySalesWindow: oneDaySalesWindowEnabled,
    ...params,
  };
  return useClientAPI(path, { params, dataParser, ...options });
};

/**
 * Hook for fetching data from the Livecommerce Client API
 *
 * Wraps https://react-query.tanstack.com/reference/useQuery and
 * all of its options can be passed to this hook as well, including
 * queryKey and queryFn which this hook otherwise sets for you.
 * @param {string} path API path (e.g.: '/stats/one-to-many/sales')
 * @param {ClientAPIOptions} options
 * @return {ClientAPIResult<any>}
 */
export const useClientAPI = (path, {
  params,
  dataParser = data => data.body,
  buffer = false,
  ...options
}) => {
  const firebase = useFirebase();
  const queryKey = useQueryKey(path, params);
  const queryFn = async () => {
    try {
      const agent = await firebase.getAgent();
      if (buffer) {
        const result = await agent.get(path, params).responseType('blob');
        return dataParser(result);
      }
      const result = await agent.get(path, params);
      return dataParser(result);
    } catch (e) {
      if (e?.response?.body?.errorCode) {
        throw new ClientAPIError(e.response.body.errorCode, e.response.body.message);
      }
      throw e;
    }
  };

  return useQuery({ queryKey, queryFn, ...options });
};

/**
 * Hook for retrieving the queryKey which is used as cache key
 * for the api request
 */
const useQueryKey = (path, params) => {
  // Sort parameters to make sure they are in the same order every time
  // (So that the cache does not reset due to change in order)
  const sortedParams = params && Object.entries(params).sort(([keyA], [keyB]) => keyA.toLowerCase() < keyB.toLowerCase() ? -1 : 1);
  const paramStrings = sortedParams && sortedParams.map(([param, value]) => `${param}=${value}`);
  const queryString = paramStrings ? `QueryString;?${paramStrings.join('&')}` : '';

  // Fetch information for the currently logged in user to be used in the query key
  // (This prevents cache bleed between users on the same computer)
  const authUser = useSelector(state => state.sessionState.authUser);
  const authString = `Auth;uid=${authUser?.uid};orgId=${authUser?.org?.id}`;

  return [path, authString, queryString];
};

export class ClientAPIError extends Error {
  constructor(errorCode, message) {
    super(message);
    this.errorCode = errorCode;
    this.name = this.constructor.name;
  }

  static get errorCodes() {
    return {
      LEADERBOARD_DATA_NOT_AVAILABLE_FOR_SELECTED_DATES: 1001,
      SHOW_MISSING_TIMEZONE_INFORMATION: 1002,
      SUMMARY_PAGE_NO_ACTIVITY_IN_PERIOD: 1003,
    };
  }
}

export const testables = {
  useQueryKey,
};

export default useClientAPI;
